import React from 'react';
import { Button, Image } from 'react-bootstrap';
import { useGoogleLogin } from '@react-oauth/google';
import { authAPI, getDeviceInfo } from '../../helpers/api_service';
import { APP_URLS } from '../../helpers/url_helper';
import sessionManager from '../../helpers/session_manager';
import { clearAllOAuthData } from '../../helpers/oauth_logout';
import { getFCMToken, isNotificationSupported } from '../../firebase';
import GmailIcon from '../../assets/icgmail.svg';

const GoogleLogin = ({ onError, onSuccess, isLoading, setIsLoading, navigate, onDeviceConflict }) => {
  // Function to proceed with login after device conflict is resolved
  const proceedWithGoogleLogin = (loginData) => {
    // Start session using session manager
    sessionManager.startSession(loginData.token);
    
    // Save additional user data for topbar
    localStorage.setItem('userData', JSON.stringify(loginData));
    localStorage.setItem('userFullName', loginData.full_name || '');
    localStorage.setItem('userProfilePic', loginData.profile_pic || '');
    
    console.log('=== SESSION CREATED ===');
    console.log('Session started successfully');
    console.log('User data saved to localStorage:', {
      userData: loginData,
      userFullName: loginData.full_name || '',
      userProfilePic: loginData.profile_pic || ''
    });
    
    // Check if user is new and redirect accordingly
    if (loginData.is_new_user === true) {
      console.log('=== NEW USER DETECTED ===');
      console.log('Redirecting to complete profile page');
      navigate('/complete-profile');
    } else {
      console.log('=== EXISTING USER ===');
      // Check subscription package
      if (!loginData.subscription_package || loginData.subscription_package.status === 'expired') {
        console.log('=== NO SUBSCRIPTION OR EXPIRED ===');
        console.log('Redirecting to newSubscription page');
        navigate('/newSubscription');
      } else {
        console.log('=== ACTIVE SUBSCRIPTION ===');
        console.log('Redirecting to dashboard');
        navigate(APP_URLS.DASHBOARD);
      }
    }
  };

  const handleGoogleSuccess = async (credentialResponse) => {
    setIsLoading(true);
    onError('');

    // Clear any existing OAuth data before starting new authentication
    clearAllOAuthData();

    try {
      console.log('=== GOOGLE AUTHENTICATION RESPONSE ===');
      console.log('Full Google credential response:', credentialResponse);
      
      let userDetails = {};
      
      // Check if we have access_token (OAuth flow) or credential (JWT flow)
      if (credentialResponse.access_token) {
        console.log('=== FETCHING USER PROFILE WITH ACCESS TOKEN ===');
        
        try {
          // Fetch user profile using Google People API
          const profileResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
            headers: {
              'Authorization': `Bearer ${credentialResponse.access_token}`
            }
          });
          
          if (profileResponse.ok) {
            const profileData = await profileResponse.json();
            console.log('=== GOOGLE PROFILE DATA ===');
            console.log('Raw profile response:', profileData);
            
            // Extract user details and format for API
            userDetails = {
              provider: "google",
              provider_id: profileData.id,
              email: profileData.email,
              name: profileData.name,
              picture: profileData.picture,
              username: profileData.email
            };
            
            console.log('=== EXTRACTED USER DETAILS ===');
            console.log('User details for backend:', userDetails);
            
            // Store in localStorage for reference (including access token for logout)
            const userDetailsWithToken = {
              ...userDetails,
              access_token: credentialResponse.access_token
            };
            localStorage.setItem('googleUserDetails', JSON.stringify(userDetailsWithToken));
            console.log('=== STORED IN LOCALSTORAGE ===');
            console.log('Google user details stored:', userDetailsWithToken);
            
          } else {
            console.error('Failed to fetch profile:', profileResponse.status, profileResponse.statusText);
            throw new Error('Failed to fetch user profile from Google');
          }
          
        } catch (profileError) {
          console.error('Error fetching profile:', profileError);
          throw profileError;
        }
        
      } else if (credentialResponse.credential) {
        // Handle JWT credential flow (fallback)
        console.log('=== DECODING JWT CREDENTIAL ===');
        try {
          const payload = JSON.parse(atob(credentialResponse.credential.split('.')[1]));
          console.log('=== DECODED GOOGLE USER DATA ===');
          console.log('Complete decoded payload:', payload);
          
          userDetails = {
            provider: "google",
            provider_id: payload.sub,
            email: payload.email,
            name: payload.name,
            picture: payload.picture,
            username: payload.email
          };
          
          console.log('=== EXTRACTED USER DETAILS ===');
          console.log('User details for backend:', userDetails);
          
        } catch (decodeError) {
          console.error('Error decoding JWT token:', decodeError);
          throw decodeError;
        }
      }
      
      console.log('=== SENDING TO BACKEND ===');
      console.log('User details being sent to backend:', userDetails);
      console.log('Device info being sent:', getDeviceInfo());
      
      // Helper function to get browser name from device info
      const getBrowserName = () => {
        const deviceInfo = JSON.parse(getDeviceInfo());
        return deviceInfo.browser_name || 'Unknown';
      };
      
      // Get FCM token if notifications are supported
      let fcmToken = null;
      if (isNotificationSupported()) {
        try {
          fcmToken = await getFCMToken();
        } catch (fcmError) {
          console.warn('Failed to get FCM token for Google login:', fcmError);
          // Continue with login even if FCM token fails
        }
      }
      
      // Add browser_name, device_type, and FCM token to user details
      const userDetailsWithDeviceInfo = {
        ...userDetails,
        browser_name: getBrowserName(),
        device_type: "web",
        fcm_token: fcmToken
      };
      
      // Call your backend API with the user details
      const data = await authAPI.socialLogin(userDetailsWithDeviceInfo);

      console.log('=== BACKEND RESPONSE ===');
      console.log('Backend response:', data);

      if (data.status === 'success') {
        console.log('=== AUTHENTICATION SUCCESS ===');
        console.log('Token received:', data.data.token);
        console.log('User data from backend:', data.data);
        console.log('Is new user:', data.data.is_new_user);
        
        // Check for device limit reached
        if (data.data.device_limit_reached === true) {
          // Show device conflict popup
          if (onDeviceConflict) {
            onDeviceConflict(data.data.active_device_sessions, data.data);
          }
          return;
        }
        
        // No device conflict, proceed with normal login
        proceedWithGoogleLogin(data.data);
      } else {
        console.log('=== AUTHENTICATION FAILED ===');
        console.log('Backend returned error:', data.message);
        onError(data.message || 'Google authentication failed. Please try again.');
      }
    } catch (err) {
      console.error('=== GOOGLE LOGIN ERROR ===');
      console.error('Network or API error:', err);
      onError('Network error. Please check your connection and try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleError = () => {
    onError('Google authentication was cancelled or failed. Please try again.');
  };

  const googleLogin = useGoogleLogin({
    onSuccess: handleGoogleSuccess,
    onError: handleGoogleError,
  });

  const handleGoogleButtonClick = () => {
    googleLogin();
  };

  return (
    <Button 
      className="login-social-btn w-50 d-flex align-items-center justify-content-center"
      onClick={handleGoogleButtonClick}
      disabled={isLoading}
    >
      <Image src={GmailIcon} alt="Google Icon" width={24} height={24} />
      <span className="ms-2">Log in with Google</span>
    </Button>
  );
};

export default GoogleLogin; 